// 計算ロジック共通：控除行生成 + ヘッダ生成 + 税集計・最終化

import type { Driver, ItemRow, Statement, Carrier, TaxSummary } from "../../types";
import { fmtMonthJp, lastOfMonth, nextMonthDay, plusMonthsDay } from "../normalize";
import { daysInService, prorateMonthlyAmount, buildProrateNote } from "./prorate";

export function buildHeader(
  driver: Driver,
  carriers: Carrier[],
  targetMonth: string
): Omit<Statement, "items" | "salesTotal" | "deductTotal" | "netTotal" | "warnings" | "status"> {
  const salesDate = lastOfMonth(targetMonth);
  const billingDate = nextMonthDay(targetMonth, 10);
  // 振込予定日：翌々月10日（40日サイト。本城さん指示 6/19。例: 5月分→7/10）
  const dueDate = plusMonthsDay(targetMonth, 2, 10);
  // 人単位1通に統合（R1）。タイトルから業者名は外す（兼任は明細内で業者を区別表示）。
  const title = `${fmtMonthJp(targetMonth + "-01")}分委託料`;
  const list = carriers.length > 0 ? carriers : (["sagawa"] as Carrier[]);
  return {
    driverId: driver.id,
    driverName: driver.name,
    carrier: list[0],
    carriers: list,
    targetMonth,
    branch: driver.branch,
    org: driver.org,
    invoiceNo: driver.invoiceNo,
    title,
    billingDate,
    dueDate,
    salesDate,
  };
}

// 控除行（携帯リース・車両リース・ガソリン・アプリ・指導料）
export function buildDeductions(driver: Driver, salesDate: string): ItemRow[] {
  const items: ItemRow[] = [];
  // 月途中入退社の日割り対象は3項目のみ（携帯・車両・アプリ。ガソリンR列・指導料は対象外。
  // 本城さん確定回答 2026-07-16・Issue #49）。在籍日数0（対象月にまったく在籍していない）
  // なら、この3項目の控除行自体を出さない。
  const service = daysInService(salesDate, driver.contractFrom, driver.contractTo);
  const skipProratedItems = service.days <= 0;
  const prorateNote = buildProrateNote(salesDate, driver.contractFrom, driver.contractTo);

  if (!skipProratedItems && driver.monthlyKeitai != null && driver.monthlyKeitai > 0) {
    const amt = prorateMonthlyAmount(driver.monthlyKeitai, salesDate, driver.contractFrom, driver.contractTo);
    items.push({
      deliveryDate: salesDate,
      name: "携帯リース料",
      unitPrice: -amt,
      quantity: 1,
      amount: -amt,
      taxRate: "10%",
      category: "deduct",
      ...(prorateNote ? { note: prorateNote } : {}),
    });
  }
  if (!skipProratedItems && driver.monthlySharyo != null && driver.monthlySharyo > 0) {
    const amt = prorateMonthlyAmount(driver.monthlySharyo, salesDate, driver.contractFrom, driver.contractTo);
    items.push({
      deliveryDate: salesDate,
      name: "車両リース料",
      unitPrice: -amt,
      quantity: 1,
      amount: -amt,
      taxRate: "10%",
      category: "deduct",
      ...(prorateNote ? { note: prorateNote } : {}),
    });
  }
  if (driver.hasGasCard) {
    items.push({
      deliveryDate: salesDate,
      name: "ガソリンカード利用料",
      unitPrice: null, // 手入力
      quantity: 1,
      amount: 0,
      taxRate: "non",
      category: "manual",
      note: "手入力（徳長様からの個別連絡値）",
    });
  }
  // マスター「ガソリンカード利用」列（R列）の定額＝カード貸与手数料。月別控除入力タブのガソリン（実費・
  // 不課税）とは別物で、こちらは課税10%（本城さん確定 2026-07-16: 5,000税抜/5,500税込。Issue #48）。
  // 実費の「ガソリンカード利用料」と二件並ぶため、名目を「ガソリンカード貸与手数料」に分離（本城さん依頼 2026-07-24）。
  if (driver.gasUsage != null && driver.gasUsage > 0) {
    items.push({
      deliveryDate: salesDate,
      name: "ガソリンカード貸与手数料",
      unitPrice: -driver.gasUsage,
      quantity: 1,
      amount: -driver.gasUsage,
      taxRate: "10%",
      category: "deduct",
      note: "マスター「ガソリンカード利用」列から自動反映（課税10%）",
    });
  }
  if (!skipProratedItems && driver.monthlyAppli != null && driver.monthlyAppli > 0) {
    const amt = prorateMonthlyAmount(driver.monthlyAppli, salesDate, driver.contractFrom, driver.contractTo);
    items.push({
      deliveryDate: salesDate,
      name: "アプリ利用料",
      unitPrice: -amt,
      quantity: 1,
      amount: -amt,
      taxRate: "10%",
      category: "deduct",
      ...(prorateNote ? { note: prorateNote } : {}),
    });
  }
  // 指導料：マスター列O「指導料」= 件数。その数だけ単価空欄行を追加
  if (driver.shidoCount != null && driver.shidoCount > 0) {
    for (let i = 0; i < driver.shidoCount; i++) {
      items.push({
        deliveryDate: salesDate,
        name: "指導料",
        unitPrice: null,
        quantity: 1,
        amount: 0,
        taxRate: "10%",
        category: "manual",
        note: "手入力（指導関係・件数連動）",
      });
    }
  }
  return items;
}

// インボイス経過措置：適格請求書発行事業者でない者（インボイス未登録ドライバー）からの仕入は
// 仕入税額控除が段階的に縮小される。控除できない分を「値引き」として支払から差し引く。
//   〜2026/9   : 控除80% → 値引き = 消費税 × 1/5
//   2026/10〜2029/9 : 控除50% → 値引き = 消費税 × 1/2
//   2029/10〜  : 控除なし → 値引き = 消費税 全額（1/1）
// ※ R8(2026)/10 で割合が変わるため、対象月から自動で分母を切り替える。
export function invoiceDiscountDivisor(targetMonth: string): number {
  if (targetMonth >= "2029-10") return 1;
  if (targetMonth >= "2026-10") return 2;
  return 5;
}

// 値引き行の識別マーカー（note に格納。名前 "値引き" での判定は誤爆するため使わない）。
// UI で表示するときは friendly な説明文に置き換える。
export const INVOICE_DISCOUNT_NOTE = "__invoice_discount__";

// インボイス未登録者の「値引き」控除行を作る。
// 基準額 = 課税売上 − 課税控除（不課税のガソリン等は含めない。本城さん指示 6/19、勝本様/橋本様PDFで検証済み）。
// 値引き = floor(基準額 × 10% ÷ 分母)。値引き行自体も課税(10%)扱い＝税基から引かれる（PDF実測と一致）。
export function buildInvoiceDiscount(
  taxableBasis: number,
  targetMonth: string,
  salesDate: string
): ItemRow | null {
  const divisor = invoiceDiscountDivisor(targetMonth);
  const discount = Math.floor(taxableBasis / (10 * divisor));
  if (discount <= 0) return null;
  return {
    deliveryDate: salesDate,
    name: "値引き",
    unitPrice: -discount,
    quantity: 1,
    amount: -discount,
    taxRate: "10%",
    category: "deduct",
    note: INVOICE_DISCOUNT_NOTE,
  };
}

// 8%（軽減税率）分の値引き行。適格請求書は税率ごとの区分記載が要件のため、
// 8%対象がある場合は値引きも税率ごとに分ける（本城さん要望 2026-08-07）。
// 8%対象が無ければ null を返す＝従来の通知書（10%のみ）の見た目・金額は一切変わらない。
export function buildInvoiceDiscount8(
  taxable8Basis: number,
  targetMonth: string,
  salesDate: string
): ItemRow | null {
  const divisor = invoiceDiscountDivisor(targetMonth);
  // 消費税相当（8%）の 1/divisor。整数どうしの積を先に取る
  const discount = Math.floor((taxable8Basis * 8) / (100 * divisor));
  if (discount <= 0) return null;
  return {
    deliveryDate: salesDate,
    name: "値引き",
    unitPrice: -discount,
    quantity: 1,
    amount: -discount,
    taxRate: "8%",
    category: "deduct",
    note: INVOICE_DISCOUNT_NOTE,
  };
}

// 月初〜月末の日付配列を生成（YYYY-MM-DD）
export function eachDayOfMonth(targetMonth: string): string[] {
  const [y, m] = targetMonth.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const out: string[] = [];
  for (let d = 1; d <= last; d++) {
    out.push(`${targetMonth}-${String(d).padStart(2, "0")}`);
  }
  return out;
}

// R1: 人単位1通の統合 — 業者別に計算済みの売上itemsを束ね、控除を1回だけ付けて1通の Statement を作る。
// これにより兼任ドライバーでも控除（携帯/車両/アプリ/ガソリン/指導料）の二重計上が構造的に起きない。
export function mergeDriverStatement(
  driver: Driver,
  salesItems: ItemRow[],
  carriers: Carrier[],
  targetMonth: string,
  opts?: { specialRule?: string | null; warnings?: string[] }
): Statement {
  const header = buildHeader(driver, carriers, targetMonth);
  const deductions = buildDeductions(driver, header.salesDate); // 人単位で1回だけ
  const warnings = opts?.warnings ?? [];
  // 値引き・税集計は finalizeStatement に一元化（手入力・臨時控除の後入れにも追随させるため）
  return finalizeStatement({
    ...header,
    items: [...salesItems, ...deductions],
    salesTotal: 0,
    deductTotal: 0,
    netTotal: 0,
    warnings,
    specialRule: opts?.specialRule ?? null,
    status: warnings.length > 0 ? "hold" : "draft",
  });
}

// 集計に算入するか（sales/deduct は常に、manual は単価入力済みのみ）
function countsInTotals(it: ItemRow): boolean {
  return (
    it.category === "sales" ||
    it.category === "deduct" ||
    (it.category === "manual" && it.unitPrice != null)
  );
}

// アイテム配列から税集計つきサマリーを作る（唯一の集計実装。旧4箇所の重複を一本化）。
//   - salesTotal / deductTotal は税抜（従来どおり＝マクロ照合の基準）
//   - netTotal は税込（= 小計 + 消費税額合計。ご請求金額と一致）
//   - 消費税は課税(10%)ネット合計に対して1回だけ切り捨て（正解PDFで検証済み）
export function computeTotals(items: ItemRow[]): {
  salesTotal: number;
  deductTotal: number;
  netTotal: number;
  taxSummary: TaxSummary;
} {
  let sales = 0;
  let deduct = 0;
  let taxable10 = 0;
  let taxable8 = 0;
  let nonTaxable = 0;
  for (const it of items) {
    if (!countsInTotals(it)) continue;
    if (it.category === "sales") sales += it.amount;
    else deduct += -it.amount; // 控除は内部amount=マイナス
    if (it.taxRate === "10%") taxable10 += it.amount;
    else if (it.taxRate === "8%") taxable8 += it.amount;
    else nonTaxable += it.amount;
  }
  // 整数円 ÷10 は浮動小数の誤差なく計算できる。負の課税ネット（支給がマイナス）は0方向へ切り捨て
  const tax10 = Math.trunc(taxable10 / 10);
  // 8%（軽減税率）も税率ごとに区分して1回だけ切り捨てる。
  // 先に整数どうしの積を取ってから割る（× 0.08 だと浮動小数の誤差で1円ずれうる）
  const tax8 = Math.trunc((taxable8 * 8) / 100);
  const subtotal = taxable10 + taxable8 + nonTaxable;
  return {
    salesTotal: sales,
    deductTotal: deduct,
    netTotal: subtotal + tax10 + tax8,
    taxSummary: { taxable10, tax10, taxable8, tax8, nonTaxable, subtotal },
  };
}

// 明細の最終化：値引き行を毎回再導出し、税集計を確定する。
// 全ての明細変更（マージ・手入力・臨時控除・ガソリン反映）は最後にここを通ること。
export function finalizeStatement(stmt: Statement): Statement {
  // 1. 既存の値引き行を除去（基準額が変わっていれば作り直すため）
  const base = stmt.items.filter((it) => it.note !== INVOICE_DISCOUNT_NOTE);
  // 2. インボイス未登録者のみ、課税ネット（値引き前）を基準に値引き行を再生成
  let items = base;
  if (!stmt.invoiceNo) {
    const basis = computeTotals(base).taxSummary;
    const discount = buildInvoiceDiscount(basis.taxable10, stmt.targetMonth, stmt.salesDate);
    // 8%対象がある場合は税率ごとに値引きを分ける（適格請求書の区分記載要件）。
    // 8%対象が無ければ null なので、従来（10%のみ）の結果は1円も変わらない。
    const discount8 = buildInvoiceDiscount8(basis.taxable8 ?? 0, stmt.targetMonth, stmt.salesDate);
    const added = [discount, discount8].filter((d): d is ItemRow => d != null);
    if (added.length > 0) items = [...base, ...added];
  }
  // 3. 最終集計
  const { salesTotal, deductTotal, netTotal, taxSummary } = computeTotals(items);
  return { ...stmt, items, salesTotal, deductTotal, netTotal, taxSummary };
}

// アイテム配列からサマリーを集計（互換ラッパー。実装は computeTotals に一本化）
export function summarize(items: ItemRow[]): { salesTotal: number; deductTotal: number; netTotal: number } {
  const { salesTotal, deductTotal, netTotal } = computeTotals(items);
  return { salesTotal, deductTotal, netTotal };
}
